/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Nullable} from "../utils/globalTypeUtils";
import {ETrackedItemTypes} from "../constants/trackedItems";
import {useAppSelector} from "../redux/hooks";
import {selectCurrentUser} from "../redux/ducks/AccessControl/slice";
import {usePostRequest} from "../api/helper";
import {TRACKED_ITEMS_ENDPOINT} from "../api/constants";
import {useCallback, useEffect} from "react";

export interface ISetRecentlyViewedParams {
  trackedItemUid: Nullable<string>;
  trackedItemType: Nullable<ETrackedItemTypes>;
  partitionKey?: string;
}

export function useSetRecentlyViewedWithDeps(params: Nullable<ISetRecentlyViewedParams>, autoSetRecentlyViewed = true, provideCallbackParams = false,
                                             depsObj: Record<string, any> = {}) {
  const trackedItemUid = params?.trackedItemUid;
  const trackedItemType = params?.trackedItemType;
  const partitionKey = params?.partitionKey;

  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const recentlyViewedFetchHandle = usePostRequest(TRACKED_ITEMS_ENDPOINT);

  const basicDeps = {gmid}
  let depsArray = []
  if (provideCallbackParams) {
    depsArray = Object.values(basicDeps)
  } else {
    const defaultDeps = {trackedItemUid, trackedItemType, partitionKey};
    const mergedDeps = {...defaultDeps, ...depsObj};
    depsArray = Object.values(mergedDeps);
  }

  const setRecentlyViewed = useCallback((callbackParams: Nullable<ISetRecentlyViewedParams> = undefined) => {
    const time = Date.now();

    let body

    if (callbackParams) {
      const {trackedItemUid, trackedItemType, partitionKey} = callbackParams;
      if (!trackedItemUid || !trackedItemType) {
        return;
      }
      body = {
        gmid,
        time,
        trackedItemUid,
        trackedItemType,
        requestType: 'addRecentlyViewed',
        ...(partitionKey && {partitionKey}),
      };
    } else {
      body = {
        gmid,
        time,
        trackedItemUid,
        trackedItemType,
        requestType: 'addRecentlyViewed',
        ...(partitionKey && {partitionKey}),
      };
    }

    recentlyViewedFetchHandle(body).then(r => {
    }).catch(e => {
      console.error(e);
    });
  }, depsArray)

  useEffect(() => {
    if (!gmid) return;

    if (autoSetRecentlyViewed) {
      setRecentlyViewed();
    }
  }, [...depsArray, setRecentlyViewed, autoSetRecentlyViewed]);

  return setRecentlyViewed;
}